import React, { useEffect, useState } from "react";
import "./styles.css";
import { Container, Table } from "react-bootstrap";
import { BLANK_STRING, VIEW_MODES } from "../../constants/appConstants";
import SwitchViewData from "../../Components/SwitchViewData/SwitchViewData";
import MultiSelector from "../../Components/MultiSelector/MultiSelector";
import { useNavigate, useParams } from "react-router-dom";
import { exportQuarterAndYear, findCurrentQuarter, getDifferenceDays, getQuarterString, getQuarterYearObject } from "../../utils/commonUtils";
import { getCAPADataAPI, getCAPADataByYearAPI } from "../../apiRequests/capa-management/getCAPADataAPI";
import { updateCAPADocumentStatusAPI } from "../../apiRequests/capa-management/updateCAPADocumentStatusAPI";
import MaskLoading from "../../Components/LoadingPage/MaskLoading";

interface CAPAManagementProps {
    isDashboard?: boolean;
    dataCAPAs?: any;
    openCAPAs?: any;
}

const CAPAManagement = (props: CAPAManagementProps) => {
    const navigate = useNavigate();
    const { period } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dataCAPAs, setDataCAPAs] = useState<any>([]);
    const [currentViewMode, setCurrentViewMode] = useState<string>(VIEW_MODES.DETAILED);
    const [selectedYear, setSelectedYear] = useState<string>(BLANK_STRING);
    const [isRenderSelectQuarter, setIsRenderSelectQuarter] = useState<boolean>(false);
    const [selectedQuarter, setSelectedQuarter] = useState<string>(BLANK_STRING);
    const [selectedYearAndQuarter, setSelectedYearAndQuarter] = useState<string[]>([]);
    const [dataKPIForMultiQuarter, setDataKPIForMultiQuarter] = useState<any[]>([]);
    const [isUpdateDocumentStatus, setIsUpdateDocumentStatus] = useState<boolean>(false);
    const [openCAPAs, setOpenCAPAs] = useState<any[]>([]);

    useEffect(() => {
        const getAllCAPAsData = async () => {
            if (currentViewMode === VIEW_MODES.TABLE && !dataCAPAs.every((data: any) => Boolean(data.isLoaded))) {
                setIsLoading(true);
                try {
                    const dataPAs = await getCAPADataAPI();
                    setDataCAPAs(dataPAs.closedCAPAs);
                } catch (error) {
                    console.error("Error fetching Purchase Acceptance data:", error);
                }
                setIsLoading(false);
            }
        };
        getAllCAPAsData();
    }, [currentViewMode]);

    useEffect(() => {
        !props.isDashboard && getCAPADataAndSetToState();
    }, []);

    useEffect(() => {
        if (props.isDashboard) {
            setDataCAPAs(props.dataCAPAs);
            setOpenCAPAs(props.openCAPAs);
            setCurrentYearAndQuarter(props.dataCAPAs);
        }
    }, [props.dataCAPAs, props.isDashboard, props.openCAPAs]);

    const setCurrentYearAndQuarter = (dataCAPAs: any) => {
        const { year, quarter } = exportQuarterAndYear(period || BLANK_STRING);

        if (year && quarter) {
            const currentCAPA = dataCAPAs.find((data: any) => data.yearCAPAs.title.includes(year));

            setSelectedYear(currentCAPA?.yearCAPAs?.id || BLANK_STRING);
            const currentQuarter = findCurrentQuarter(currentCAPA?.quarterCAPAs, year, quarter);

            setSelectedQuarter(currentQuarter || BLANK_STRING);
        } else {
            const currentYear = new Date().getFullYear();
            const currentCAPA = dataCAPAs.find((data: any) => data.yearCAPAs.title.includes(currentYear));
            setSelectedYear(currentCAPA?.yearCAPAs?.id);
            const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
            const selectedQuarter = findCurrentQuarter(currentCAPA?.quarterCAPAs, currentYear, currentQuarter);
            setSelectedQuarter(selectedQuarter || BLANK_STRING);
            const quarterString = getQuarterString(currentQuarter);
            setSelectedYearAndQuarter([`CAPA Q${currentQuarter},${currentYear}`]);
            !props.isDashboard && navigate(`/capa-management/${quarterString}-${currentYear}`, { replace: true });
        }
    };

    useEffect(() => {
        if (selectedYear) {
            setIsRenderSelectQuarter(true);
        }
    }, [selectedYear]);

    useEffect(() => {
        const quarterData = dataCAPAs.reduce((acc: any[], item: any) => {
            const selectedQuarters = Object.keys(item.quarterCAPAs)
                .filter((quarterKey: string) => selectedYearAndQuarter.includes(quarterKey))
                .map((quarterKey: string) => ({ [quarterKey]: item.quarterCAPAs[quarterKey] }));
            const sortedSelectedQuarters = selectedQuarters.sort((a, b) => {
                const keyA = Object.keys(a)[0];
                const keyB = Object.keys(b)[0];
                return sortStringQuarterAndYear(keyA, keyB);
            });
            return [...acc, ...sortedSelectedQuarters];
        }, []);

        if (quarterData) {
            const calculatedData = quarterData.map((data: any, index: number) => {
                const { KPI, passed, total } = calculateMetrics(data[selectedYearAndQuarter[index]]);
                return { KPI, passed, total };
            });
            setDataKPIForMultiQuarter(calculatedData);
        }
    }, [selectedYearAndQuarter, dataCAPAs]);

    const getCAPADataAndSetToState = async () => {
        setIsLoading(true);
        const { year: periodYear } = exportQuarterAndYear(period || BLANK_STRING);
        const targetYear = periodYear || new Date().getFullYear();
        const results = await getCAPADataByYearAPI(targetYear.toString());

        setIsUpdateDocumentStatus(false);
        setDataCAPAs(results.closedCAPAs);
        setOpenCAPAs(results.openCAPAs);
        if (results.closedCAPAs.length && !isUpdateDocumentStatus) {
            setCurrentYearAndQuarter(results.closedCAPAs);
        }
        setIsLoading(false);
    };

    const handleLoadCAPAsDataForOneYear = async (yearId: string, selectedYearData: any) => {
        try {
            const year = parseInt(selectedYearData.yearCAPAs.title, 10);
            const result = await getCAPADataByYearAPI(year.toString());

            const updatedDataCAPAs = mergeClosedCAPAs(result, {
                closedCAPAs: dataCAPAs,
                openCAPAs: openCAPAs,
            });

            selectedYearData = updatedDataCAPAs.closedCAPAs.find((data: any) => data.yearCAPAs.id === yearId);

            setDataCAPAs(updatedDataCAPAs.closedCAPAs);
            return selectedYearData;
        } catch (error) {
            console.error("Failed to fetch purchase acceptance data", error);
        } finally {
        }
    };

    function mergeClosedCAPAs(data1: any, data2: any) {
        const mergedData = { ...data1 };

        data2.closedCAPAs.forEach((data2CAPA: any) => {
            const matchedCAPA = mergedData.closedCAPAs?.find((data1CAPA: any) => data1CAPA.yearCAPAs.id === data2CAPA.yearCAPAs.id);

            if (matchedCAPA) {
                matchedCAPA.quarterCAPAs = {
                    ...(matchedCAPA.quarterCAPAs || {}),
                    ...(data2CAPA.quarterCAPAs || {}),
                };
                matchedCAPA.isLoaded = true;
            } else {
                mergedData.closedCAPAs.push(data2CAPA);
            }
        });

        return mergedData;
    }

    const handleYearChange = async (event: any) => {
        setIsLoading(true);
        const yearId = event.target.value;
        setSelectedYear(yearId);
        let selectedYearData = dataCAPAs.find((data: any) => data.yearCAPAs.id === event.target.value);

        if (!Boolean(selectedYearData.isLoaded)) {
            selectedYearData = await handleLoadCAPAsDataForOneYear(event.target.value, selectedYearData);
        }

        const listQuarters = Object.keys(selectedYearData.quarterCAPAs || {});
        const latestQuarter = listQuarters[listQuarters.length - 1];
        setSelectedQuarter(latestQuarter || BLANK_STRING);

        if (latestQuarter) {
            const { quarter, year } = getQuarterYearObject(listQuarters[listQuarters.length - 1]);
            const quarterInString = getQuarterString(quarter);
            !props.isDashboard && navigate(`/capa-management/${quarterInString}-${year}`, { replace: true });
        }
        setIsLoading(false);
    };

    const renderSelectCAPAsYear = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="year">
                    Select Year:
                </label>
                <select
                    className="select"
                    id="year"
                    value={selectedYear}
                    onChange={(event) => {
                        handleYearChange(event);
                    }}
                >
                    <option disabled value="">
                        Select Year
                    </option>
                    {dataCAPAs.map((data: any) => (
                        <option key={data.yearCAPAs.title} value={data.yearCAPAs.id}>
                            {data.yearCAPAs.title}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const handleQuarterChange = (event: any) => {
        setSelectedQuarter(event.target.value);
        const { quarter, year } = getQuarterYearObject(event.target.value);
        const quarterInString = getQuarterString(quarter);
        !props.isDashboard && navigate(`/capa-management/${quarterInString}-${year}`, { replace: true });
    };

    const renderSelectCAPAsQuarter = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="quarter">
                    Select Quarter:
                </label>
                <select
                    className="select"
                    id="quarter"
                    value={selectedQuarter}
                    onChange={(event) => {
                        handleQuarterChange(event);
                    }}
                >
                    <option value="">Select Quarter</option>
                    {dataCAPAs.map((data: any) => {
                        if (data.yearCAPAs.id === selectedYear) {
                            return Object.keys(data.quarterCAPAs).map((quarter) => (
                                <option key={quarter} value={quarter}>
                                    {quarter}
                                </option>
                            ));
                        }
                    })}
                </select>
            </div>
        );
    };

    const calculateMetrics = (dataCAPAsInSelectedQuarter: any[]) => {
        const passed = dataCAPAsInSelectedQuarter.reduce((acc: number, data: any) => {
            if (data.status === "PASSED") {
                return acc + 1;
            }
            return acc;
        }, 0);
        const total = dataCAPAsInSelectedQuarter.length;
        const KPI = ((passed / total) * 100).toFixed(2);

        return { KPI, passed, total };
    };

    const renderCAPAsDataTable = () => {
        const dataCAPAsInSelectedYear = dataCAPAs.find((data: any) => data.yearCAPAs.id === selectedYear);

        const dataCAPAsInSelectedQuarter = dataCAPAsInSelectedYear.quarterCAPAs[selectedQuarter];
        const { KPI, passed, total } = calculateMetrics(dataCAPAsInSelectedQuarter);

        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{KPI}%</td>
                        <td>{passed}</td>
                        <td>{total - passed}</td>
                        <td>{total}</td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    const renderCAPAsFailed = () => {
        const dataCAPAsInSelectedYear = dataCAPAs.find((data: any) => data.yearCAPAs.id === selectedYear);
        const dataCAPAsInSelectedQuarter = dataCAPAsInSelectedYear.quarterCAPAs[selectedQuarter];
        const failedCAPAs = dataCAPAsInSelectedQuarter.filter((data: any) => data.status === "FAILED");
        return (
            Boolean(failedCAPAs.length) && (
                <Container>
                    <p>
                        <b>Failed CAPAs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Nonconformance Effective on</th>
                                <th>CAPA Effective on</th>
                                <th>Difference days</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failedCAPAs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.effectiveOnNC).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnCAPA).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.effectiveOnNC, data.effectiveOnCAPA)}</td>
                                    <td>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                updateCAPADocumentStatus(data.id, "PASSED");
                                            }}
                                        >
                                            Mark as Passed
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const updateCAPADocumentStatus = async (id: string, status: string) => {
        const document = await updateCAPADocumentStatusAPI(id, status);
        const newData = dataCAPAs.map((data: any) => {
            if (data.yearCAPAs.id === selectedYear) {
                data.quarterCAPAs[selectedQuarter] = data.quarterCAPAs[selectedQuarter].map((row: any) => {
                    if (row.id === id) {
                        return {
                            ...row,
                            status: document.status,
                            isStatusUpdated: true,
                        };
                    }
                    return row;
                });
            }
            return data;
        });
        setIsUpdateDocumentStatus(true);
        setDataCAPAs(newData);
    };

    const renderCAPAPassed = () => {
        const dataCAPAsInSelectedYear = dataCAPAs.find((data: any) => data.yearCAPAs.id === selectedYear);
        const dataCAPAsInSelectedQuarter = dataCAPAsInSelectedYear.quarterCAPAs[selectedQuarter];
        const passedCAPAs = dataCAPAsInSelectedQuarter.filter((data: any) => data.status === "PASSED");
        return (
            Boolean(passedCAPAs.length) && (
                <Container>
                    <p>
                        <b>Passed CAPAs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Nonconformance Effective on</th>
                                <th>CAPA Effective on</th>
                                <th>Difference days</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {passedCAPAs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.effectiveOnNC).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnCAPA).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.effectiveOnNC, data.effectiveOnCAPA)}</td>
                                    <td>
                                        {data.isStatusUpdated && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    updateCAPADocumentStatus(data.id, "FAILED");
                                                }}
                                            >
                                                Mark as Failed
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const renderLoading = () => {
        return <MaskLoading></MaskLoading>;
    };
    function extractAllQuarterKeys(data: any[] = dataCAPAs) {
        const allQuarterKeys: any = [];
        data.forEach((item) => {
            if (item && item.quarterCAPAs) {
                const quarterKeys = Object.keys(item.quarterCAPAs);
                allQuarterKeys.push(...quarterKeys);
            }
        });
        return allQuarterKeys.sort(sortStringQuarterAndYear);
    }

    const sortStringQuarterAndYear = (a: string, b: string): number => {
        const [quarterA, yearA] = a.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));
        const [quarterB, yearB] = b.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));

        if (yearA > yearB) {
            return -1;
        } else if (yearA < yearB) {
            return 1;
        } else {
            return parseInt(quarterB) - parseInt(quarterA);
        }
    };

    const renderTableMultiQuarter = () => {
        return (
            <Table>
                <thead>
                    <tr>
                        <th></th>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    {dataKPIForMultiQuarter.map((data: any, index: number) => (
                        <tr key={selectedYearAndQuarter[index]}>
                            <td>{selectedYearAndQuarter[index]}</td>
                            <td>{data.KPI}%</td>
                            <td>{data.passed}</td>
                            <td>{data.total - data.passed}</td>
                            <td>{data.total}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const renderOpenCAPAs = () => {
        const currentYear = new Date().getFullYear();
        const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
        const { quarter, year } = getQuarterYearObject(selectedQuarter);
        const isCurrentQuarterAndYear = year === currentYear && quarter === currentQuarter;
        return (
            Boolean(openCAPAs.length) &&
            isCurrentQuarterAndYear && (
                <Container>
                    <p>
                        <b>Open CAPAs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Nonconformance Effective on</th>
                                <th>CAPA Effective on</th>
                                <th>Difference days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {openCAPAs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.effectiveOnNC).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnCAPA).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.effectiveOnNC, data.effectiveOnCAPA)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const renderDetailedView = () => {
        return (
            <>
                {renderSelectCAPAsYear()}
                {isRenderSelectQuarter && renderSelectCAPAsQuarter()}
                {selectedQuarter && !isLoading && renderCAPAsDataTable()}
                {selectedQuarter && !isLoading && renderCAPAsFailed()}
                {selectedQuarter && !isLoading && renderCAPAPassed()}
                {renderOpenCAPAs()}
            </>
        );
    };

    const renderTableViewMultiQuarter = () => {
        return (
            <>
                <MultiSelector
                    dataOptions={extractAllQuarterKeys()}
                    currentSelected={selectedYearAndQuarter.sort(sortStringQuarterAndYear)}
                    onChange={(data: any[]) => setSelectedYearAndQuarter(data.sort(sortStringQuarterAndYear))}
                    title="Select Year And Quarter"
                ></MultiSelector>
                {selectedYearAndQuarter.length > 0 && renderTableMultiQuarter()}
            </>
        );
    };

    const renderMainContentOfCAPAManagement = () => {
        return (
            <>
                {isLoading && renderLoading()}
                <SwitchViewData currentViewMode={currentViewMode} setCurrentViewMode={(view) => setCurrentViewMode(view)}></SwitchViewData>
                {currentViewMode === VIEW_MODES.DETAILED && renderDetailedView()}
                {currentViewMode === VIEW_MODES.TABLE && renderTableViewMultiQuarter()}
            </>
        );
    };
    return <Container>{renderMainContentOfCAPAManagement()}</Container>;
};

export default CAPAManagement;
